/**
 * VeyroCam — Reveal-on-scroll
 *
 * Adds `.is-revealed` to `[data-vc-reveal]` elements as they enter the
 * viewport. Deliberately tiny and dependency-free: this runs on every page,
 * so its cost is paid on every page.
 *
 * Design notes
 * ------------
 * - Elements are hidden by CSS only under `html.js`, which is set inline in
 *   <head>. If this script fails to load, nothing is ever hidden.
 * - `prefers-reduced-motion` is respected by revealing everything up front
 *   rather than by shortening the animation, so nothing moves at all.
 * - `data-vc-reveal-group` staggers direct children by a fixed step. The
 *   stagger is capped so a 20-card grid does not take 3 seconds to appear.
 */

(function () {
  'use strict';

  var REVEAL_ATTR = '[data-vc-reveal]';
  var STAGGER_STEP = 90;   // ms between siblings
  var STAGGER_MAX = 540;   // ms ceiling, ~6 items

  var prefersReducedMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  ).matches;

  function revealAll() {
    document.querySelectorAll(REVEAL_ATTR).forEach(function (el) {
      el.classList.add('is-revealed');
    });
  }

  /**
   * Assign staggered transition delays to the children of any reveal group.
   * Done once at init rather than per-intersection so the delay is stable if
   * an element leaves and re-enters the viewport.
   */
  function applyStagger() {
    document
      .querySelectorAll('[data-vc-reveal-group]')
      .forEach(function (group) {
        var step = parseInt(group.dataset.vcRevealGroup, 10) || STAGGER_STEP;
        var items = group.querySelectorAll(':scope > ' + REVEAL_ATTR);

        items.forEach(function (item, index) {
          var delay = Math.min(index * step, STAGGER_MAX);
          item.style.setProperty('--vc-reveal-delay', delay + 'ms');
        });
      });
  }

  function init() {
    // No observer support, or the visitor asked for less motion: show it all.
    if (prefersReducedMotion || !('IntersectionObserver' in window)) {
      revealAll();
      return;
    }

    applyStagger();

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-revealed');
          // One-shot: never re-hide, and stop paying observation cost.
          observer.unobserve(entry.target);
        });
      },
      {
        // Fire slightly before the element is fully on screen so the
        // transition has already begun by the time it is comfortably visible.
        rootMargin: '0px 0px -12% 0px',
        threshold: 0.05
      }
    );

    document.querySelectorAll(REVEAL_ATTR).forEach(function (el) {
      observer.observe(el);
    });

    // The Theme Editor injects and re-renders sections after load. Re-scan so
    // freshly added blocks animate instead of staying invisible.
    document.addEventListener('shopify:section:load', function (event) {
      applyStagger();
      event.target.querySelectorAll(REVEAL_ATTR).forEach(function (el) {
        observer.observe(el);
      });
    });

    // In the editor, a merchant selecting a block must see it immediately —
    // waiting for a scroll would look broken.
    document.addEventListener('shopify:block:select', function (event) {
      event.target.classList.add('is-revealed');
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
